import { useState, useEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, LogIn, Check, Calendar, ChevronDown, ChevronUp, Clock, XCircle, CheckCircle, Loader2, PartyPopper } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useCommitments } from "@/hooks/useCommitments";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/useRoles";
import { Permission } from "@/types";
import { getAttendanceStatus, checkIn, checkOut, autoMarkAbsentAfterShift, markHolidayAttendance } from "@/services/attendanceService";
import { getUserById } from "@/services/userService";
import { getOfficeById } from "@/services/officeService";
import { getCurrentLocation, isWithinOfficeRange, checkIfLate, formatCheckInTime } from "@/lib/locationUtils";
import { LocationCheckResult, ManualCheckInReason } from "@/types/attendance";
import { Office, DEFAULT_OFFICE_ATTENDANCE } from "@/types/office";
import { isHoliday } from "@/lib/holidayUtils";
import { AbsentReasonDialog } from "./AbsentReasonDialog";
import { AttendanceReasonDialog } from "./AttendanceReasonDialog";
import { format } from "date-fns";
import { useModal } from "@/contexts/ModalContext";

interface DailyTracking {
  loginFilesAchieved: number;
  loginAmountAchieved: number;
  disbursementFilesAchieved: number;
  disbursementAmountAchieved: number;
}

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

interface DailyCommitmentCardProps {
  onCommitmentChange?: () => void;
  disableCollapse?: boolean;
  /** Called after attendance is successfully marked (check-in, check-out, or absent). */
  onAttendanceMarked?: () => void;
}

export function DailyCommitmentCard({ onCommitmentChange, disableCollapse = false, onAttendanceMarked }: DailyCommitmentCardProps) {
  const { user } = useAuth();
  const { hasPermission } = usePermissions(user?.roles || []);
  const { registerModal, unregisterModal } = useModal();
  
  const { 
    dailyCommitment, 
    monthlyCommitment, 
    loading,
    monthlyEditable,
    saveDailyCommitment,
    saveMonthlyCommitment 
  } = useCommitments();
  
  // Attendance state
  const [attendanceStatus, setAttendanceStatus] = useState<{
    hasCheckedIn: boolean;
    hasCheckedOut: boolean;
    isAbsent: boolean;
    status: string;
    checkInTime?: Date;
    checkOutTime?: Date;
    isLate?: boolean;
  } | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [absentDialogOpen, setAbsentDialogOpen] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [pendingCheckInData, setPendingCheckInData] = useState<{
    location: any;
    isLate: boolean;
    isOutsideRange: boolean;
    distance: number;
    checkInTime: Date;
  } | null>(null);
  const [userOfficeId, setUserOfficeId] = useState<string>('');
  const [userOfficeName, setUserOfficeName] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userOffice, setUserOffice] = useState<Office | null>(null);
  const [todayHoliday, setTodayHoliday] = useState<{ isHoliday: boolean; reason?: string }>({ isHoliday: false });
  
  const [activeTab, setActiveTab] = useState<"daily" | "monthly">("daily");
  const [isCollapsed, setIsCollapsed] = useState(disableCollapse ? false : true); // Start collapsed on mobile unless disabled
  const [dailyTracking, setDailyTracking] = useState<DailyTracking>({
    loginFilesAchieved: 0,
    loginAmountAchieved: 0,
    disbursementFilesAchieved: 0,
    disbursementAmountAchieved: 0,
  });
  
  const [dailyLoginFiles, setDailyLoginFiles] = useState("");
  const [dailyLoginAmount, setDailyLoginAmount] = useState("");
  const [dailyLoginUnit, setDailyLoginUnit] = useState<"lakh" | "crore">("lakh");
  const [dailyDisbursementFiles, setDailyDisbursementFiles] = useState("");
  const [dailyDisbursementAmount, setDailyDisbursementAmount] = useState("");
  const [dailyDisbursementUnit, setDailyDisbursementUnit] = useState<"lakh" | "crore">("lakh");

  // Monthly form state
  const [monthlyLoginFiles, setMonthlyLoginFiles] = useState("");
  const [monthlyLoginAmount, setMonthlyLoginAmount] = useState("");
  const [monthlyLoginUnit, setMonthlyLoginUnit] = useState<"lakh" | "crore">("crore");
  const [monthlyDisbursementFiles, setMonthlyDisbursementFiles] = useState("");
  const [monthlyDisbursementAmount, setMonthlyDisbursementAmount] = useState("");
  const [monthlyDisbursementUnit, setMonthlyDisbursementUnit] = useState<"lakh" | "crore">("crore");

  // Ref to hold office unsubscribe function for live holiday sync
  const officeUnsubRef = useRef<(() => void) | null>(null);

  // Live office subscription — updates todayHoliday whenever admin changes office holiday config
  useEffect(() => {
    if (!userOfficeId) return;

    // Clean up any previous subscription
    officeUnsubRef.current?.();

    const officeDocRef = doc(db, 'offices', userOfficeId);
    const unsub = onSnapshot(officeDocRef, (snap) => {
      if (snap.exists()) {
        const rawOffice = snap.data();
        const officeData = { id: snap.id, ...rawOffice } as unknown as Office;
        setUserOffice(officeData);
        setUserOfficeName(officeData.name || '');
        setTodayHoliday(isHoliday(new Date(), officeData.holidayConfig, true));
      }
    }, (err) => {
      console.warn('Office live sync error:', err);
    });

    officeUnsubRef.current = unsub;
    return () => {
      unsub();
      officeUnsubRef.current = null;
    };
  }, [userOfficeId]);

  // Load daily tracking from localStorage (temporary - should come from leads)
  useEffect(() => {
    if (!reasonDialogOpen) return;
    registerModal('daily-commitment-reason-dialog', () => setReasonDialogOpen(false), 2);
    return () => unregisterModal('daily-commitment-reason-dialog');
  }, [reasonDialogOpen, registerModal, unregisterModal]);

  useEffect(() => {
    if (!absentDialogOpen) return;
    registerModal('daily-commitment-absent-dialog', () => setAbsentDialogOpen(false), 2);
    return () => unregisterModal('daily-commitment-absent-dialog');
  }, [absentDialogOpen, registerModal, unregisterModal]);

  // Load daily tracking from localStorage (temporary - should come from leads)
  useEffect(() => {
    const todayKey = new Date().toISOString().split('T')[0];
    const savedDailyTracking = localStorage.getItem(`daily_tracking_${todayKey}`);
    
    if (savedDailyTracking) {
      setDailyTracking(JSON.parse(savedDailyTracking));
    }
  }, []);

  // Recovery mechanism for app closure during check-in (Issue 4)
  useEffect(() => {
    const pendingData = localStorage.getItem('attendance_check_in_pending');
    if (pendingData) {
      try {
        const { userId, timestamp } = JSON.parse(pendingData);
        const ageMinutes = (Date.now() - timestamp) / (1000 * 60);
        
        // Only recover if less than 5 minutes old and same user
        if (ageMinutes < 5 && userId === user?.userId) {
          toast.info('Resuming interrupted check-in. Please try again.', {
            duration: 5000,
          });
        }
        // Always clear old pending data
        localStorage.removeItem('attendance_check_in_pending');
      } catch (error) {
        // Invalid data, just clear it
        localStorage.removeItem('attendance_check_in_pending');
      }
    }
  }, [user?.userId]);

  // Load user data and attendance status
  useEffect(() => {
    async function loadUserAndAttendance() {
      if (!user?.userId) return;

      // Prevent multiple runs per session (Issue 13)
      const today = format(new Date(), 'yyyy-MM-dd');
      const sessionKey = `attendance_auto_checked_${user.userId}_${today}`;
      const alreadyChecked = sessionStorage.getItem(sessionKey);

      try {
        // Load user data for office assignment
        const userData = user.documentId ? await getUserById(user.documentId) : null;
        let officeData: Office | null = null;

        if (userData) {
          setUserName(userData.name);
          if (userData.officeId) {
            setUserOfficeId(userData.officeId);
            // Load office data
            officeData = await getOfficeById(userData.officeId);
            if (officeData) {
              setUserOfficeName(officeData.name);
              setUserOffice(officeData);
              // Check if today is a holiday for this office
              setTodayHoliday(isHoliday(new Date(), officeData.holidayConfig));
            }
          }
        }

        // Load attendance status
        const status = await getAttendanceStatus(user.userId);
        setAttendanceStatus(status);

        // Only run auto-mark logic once per session (Issue 13)
        if (alreadyChecked) return;

        // On holidays: auto-mark holiday attendance (no auto-absent)
        const holidayCheck = isHoliday(new Date(), officeData?.holidayConfig);
        if (holidayCheck.isHoliday) {
          if (userData && userData.officeId && !status.hasCheckedIn && !status.isAbsent && status.status !== 'holiday') {
            await markHolidayAttendance(
              user.userId,
              userData.name,
              userData.officeId,
              officeData?.name || userData.officeId,
              holidayCheck.reason
            );
            const newStatus = await getAttendanceStatus(user.userId);
            setAttendanceStatus(newStatus);
            sessionStorage.setItem(sessionKey, 'true');
          }
          return; // Skip auto-absent logic on holidays
        }

        // Auto-mark absent logic - with improved timing check (Issue 8)
        if (userData && userData.officeId && !status.hasCheckedIn && !status.isAbsent) {
          const settings = officeData?.attendanceSettings ?? DEFAULT_OFFICE_ATTENDANCE;
          
          // Only attempt auto-absent if we're genuinely past shift end + buffer
          const [endH, endM] = settings.shiftEndTime.split(':').map(Number);
          const now = new Date();
          const shiftEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endH, endM);
          const bufferMs = 30 * 60 * 1000; // 30-minute buffer
          
          // Only run if genuinely past the buffer time (Issue 8)
          if (now.getTime() > shiftEnd.getTime() + bufferMs) {
            const wasAutoMarked = await autoMarkAbsentAfterShift(
              user.userId,
              userData.name,
              userData.officeId,
              officeData?.name || userData.officeId,
              settings.shiftEndTime
            );
            if (wasAutoMarked) {
              // Save zero daily commitment for auto-absent
              try {
                const { saveDailyCommitment: saveDailyCommitmentSvc } = await import("@/services/commitmentService");
                await saveDailyCommitmentSvc(user.userId, today, {
                  loginFiles: 0,
                  loginAmount: 0,
                  disbursementFiles: 0,
                  disbursementAmount: 0,
                });
              } catch {}
              // Refresh attendance status
              const newStatus = await getAttendanceStatus(user.userId);
              setAttendanceStatus(newStatus);
              sessionStorage.setItem(sessionKey, 'true');
            }
          }
        }
        
        // Mark as checked even if no action taken
        sessionStorage.setItem(sessionKey, 'true');
      } catch (error) {
        console.error('Error loading user/attendance data:', error);
      }
    }

    loadUserAndAttendance();
  }, [user]);

  // Auto-collapse when daily commitment is filled (unless collapse is disabled)
  useEffect(() => {
    if (dailyCommitment && !disableCollapse) {
      setIsCollapsed(true);
    }
  }, [dailyCommitment, disableCollapse]);

  // Check if user has permission to access commitment section (after all hooks)
  // In dialog mode (disableCollapse=true), always render — user explicitly clicked to open the dialog.
  // The permission gate only hides the card on the homepage.
  if (!disableCollapse && !hasPermission(Permission.MANAGE_COMMITMENT)) {
    return null; // Hide component for users without permission
  }

  const handleMarkPresent = async () => {
    if (!user) {
      toast.error('Please wait, loading user data...');
      return;
    }
    
    if (!user.userId) {
      toast.error('User ID not found');
      return;
    }

    if (!userOfficeId) {
      toast.error('You are not assigned to an office. Please contact admin.');
      return;
    }

    // Check network connectivity (Issue 2)
    if (!navigator.onLine) {
      toast.error('No internet connection. Please check your network and try again.');
      return;
    }

    setCheckingIn(true);

    try {
      // Save daily commitment first if not already saved
      if (!dailyCommitment) {
        // Fix commitment validation - require ALL fields (Issue 14)
        const hasAllFields = dailyLoginFiles && dailyLoginAmount && 
                            dailyDisbursementFiles && dailyDisbursementAmount;
        
        if (!hasAllFields) {
          toast.error('Please fill all daily commitment fields before marking attendance');
          setCheckingIn(false);
          return;
        }

        // Atomic commitment save - must succeed before attendance (Issue 5)
        try {
          const loginAmount = parseFloat(dailyLoginAmount) * 
            (dailyLoginUnit === 'crore' ? 10000000 : 100000);
          const disbursementAmount = parseFloat(dailyDisbursementAmount) * 
            (dailyDisbursementUnit === 'crore' ? 10000000 : 100000);

          await saveDailyCommitment({
            loginFiles: parseInt(dailyLoginFiles),
            loginAmount,
            disbursementFiles: parseInt(dailyDisbursementFiles),
            disbursementAmount,
          });

          // Wait for Firestore write to complete
          await new Promise(resolve => setTimeout(resolve, 800));
        } catch (error) {
          toast.error("Failed to save commitment. Please try again.");
          setCheckingIn(false);
          return;
        }
      }
      
      // Save pending state to localStorage for recovery (Issue 4)
      localStorage.setItem('attendance_check_in_pending', JSON.stringify({
        userId: user.userId,
        timestamp: Date.now(),
      }));

      // Get current location with improved GPS handling
      toast.info('Acquiring GPS location...');
      const userLocation = await getCurrentLocation(15000, 2, 100);
      const checkInTime = new Date();
      
      // Load office location
      const office = await getOfficeById(userOfficeId);
      if (!office?.geoLocation || (office.geoLocation.latitude === 0 && office.geoLocation.longitude === 0)) {
        toast.error('Office location not configured');
        setCheckingIn(false);
        return;
      }

      // Validate location (per-office radius or default 200m)
      const officeSettings = userOffice?.attendanceSettings ?? DEFAULT_OFFICE_ATTENDANCE;
      const locationCheck = isWithinOfficeRange(
        userLocation,
        office.geoLocation,
        officeSettings.officeRadius
      );

      // Check if late (based on per-office shift times)
      const lateCheck = checkIfLate(
        officeSettings.shiftStartTime,
        officeSettings.gracePeriodMinutes,
        officeSettings.lateThresholdMinutes
      );

      const isWithinRange = locationCheck.isWithinRange;
      const isLate = lateCheck.isLate;

      // Scenario 1: Within range + On time -> Direct acceptance
      if (isWithinRange && !isLate) {
        await checkIn({
          userId: user.userId,
          userName: userName || user.userId,
          date: format(checkInTime, 'yyyy-MM-dd'),
          checkInTime,
          location: userLocation,
          officeId: userOfficeId,
          officeName: userOfficeName || userOfficeId,
          isWithinRange: true,
          distance: locationCheck.distance,
          isLate: false,
        });

        // Refresh attendance status
        const newStatus = await getAttendanceStatus(user.userId);
        setAttendanceStatus(newStatus);

        // Clear pending state on success (Issue 4)
        localStorage.removeItem('attendance_check_in_pending');
        setCheckingIn(false);
        onAttendanceMarked?.();
      } else {
        // Scenario 2, 3, or 4: Need reason dialog
        setPendingCheckInData({
          location: userLocation,
          isLate,
          isOutsideRange: !isWithinRange,
          distance: locationCheck.distance,
          checkInTime,
        });
        setReasonDialogOpen(true);
        setCheckingIn(false);
      }
    } catch (error: any) {
      setCheckingIn(false);
      // Clear pending state on error (Issue 4)
      localStorage.removeItem('attendance_check_in_pending');
      
      // Enhanced error messaging (Issue 18)
      if (error.message.includes('denied')) {
        toast.error('Location permission denied. Please enable location access in your browser settings.', {
          duration: 5000,
        });
      } else if (error.message.includes('unavailable')) {
        toast.error('Location services unavailable. Please enable GPS and try again.', {
          duration: 5000,
        });
      } else if (error.message.includes('timeout')) {
        toast.error('Location request timed out. Please check your GPS signal and try again.', {
          duration: 5000,
        });
      } else if (error.message.includes('network') || !navigator.onLine) {
        toast.error('Network error. Please check your connection and try again.', {
          duration: 5000,
        });
      } else {
        toast.error(error.message || 'Failed to check in. Please try again.', {
          duration: 5000,
        });
      }
    }
  };

  const handleConfirmAttendanceWithReason = async (
    lateReason: string,
    locationReason: string
  ) => {
    if (!user?.userId || !pendingCheckInData) return;

    try {
      await checkIn({
        userId: user.userId,
        userName: userName || user.userId,
        date: format(pendingCheckInData.checkInTime, 'yyyy-MM-dd'),
        checkInTime: pendingCheckInData.checkInTime,
        location: pendingCheckInData.location,
        officeId: userOfficeId,
        officeName: userOfficeName || userOfficeId,
        isWithinRange: !pendingCheckInData.isOutsideRange,
        distance: pendingCheckInData.distance,
        isLate: pendingCheckInData.isLate,
        lateReason: pendingCheckInData.isLate ? lateReason : undefined,
        locationReason: pendingCheckInData.isOutsideRange ? locationReason : undefined,
      });

      // Refresh attendance status
      const newStatus = await getAttendanceStatus(user.userId);
      setAttendanceStatus(newStatus);

      setPendingCheckInData(null);
      onAttendanceMarked?.();
    } catch (error: any) {
      console.error('Failed to check in with reason:', error);
      toast.error(error.message || 'Failed to mark attendance');
    }
  };

  const handleMarkAbsent = () => {
    if (!user) {
      toast.error('Please wait, loading user data...');
      return;
    }
    
    if (!user.userId) {
      toast.error('User ID not found');
      return;
    }

    if (!userName || !userOfficeId || !userOfficeName) {
      toast.error('User data not fully loaded. Please wait a moment and try again.');
      return;
    }

    setAbsentDialogOpen(true);
  };

  const handleCheckOut = async () => {
    if (!user) {
      toast.error('Please wait, loading user data...');
      return;
    }
    
    if (!user.userId) {
      toast.error('User ID not found');
      return;
    }

    if (!userOfficeId) {
      toast.error('You are not assigned to an office. Please contact admin.');
      return;
    }

    // Check network connectivity (Issue 6)
    if (!navigator.onLine) {
      toast.error('No internet connection. Please check your network and try again.');
      return;
    }

    setCheckingOut(true);

    try {
      // Get current location
      toast.info('Acquiring GPS location...');
      const userLocation = await getCurrentLocation(15000, 2, 100);
      
      // Load office location
      const office = await getOfficeById(userOfficeId);
      if (!office?.geoLocation || (office.geoLocation.latitude === 0 && office.geoLocation.longitude === 0)) {
        toast.error('Office location not configured');
        setCheckingOut(false);
        return;
      }

      // Validate location
      const officeSettings = userOffice?.attendanceSettings ?? DEFAULT_OFFICE_ATTENDANCE;
      const locationCheck = isWithinOfficeRange(
        userLocation,
        office.geoLocation,
        officeSettings.officeRadius
      );

      await checkOut(user.userId, {
        checkOutTime: new Date(),
        checkOutLocation: userLocation,
        isWithinRange: locationCheck.isWithinRange,
        distance: locationCheck.distance,
      });

      // Refresh attendance status
      const newStatus = await getAttendanceStatus(user.userId);
      setAttendanceStatus(newStatus);
    } catch (error: any) {
      // Enhanced error messaging (Issue 16)
      if (error.message.includes('network') || !navigator.onLine) {
        toast.error('Network error. Please check your connection and try again.', {
          duration: 5000,
        });
      } else {
        toast.error(error.message || 'Failed to check out. Please try again.', {
          duration: 5000,
        });
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const handleAbsentMarked = async () => {
    if (!user?.userId) return;

    // Refresh attendance status
    const newStatus = await getAttendanceStatus(user.userId);
    setAttendanceStatus(newStatus);
    onAttendanceMarked?.();
  };

  const handleDailySubmit = async () => {
    if (!dailyLoginFiles || !dailyLoginAmount || !dailyDisbursementFiles || !dailyDisbursementAmount) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const loginMultiplier = dailyLoginUnit === "crore" ? 10000000 : 100000;
      const disbursementMultiplier = dailyDisbursementUnit === "crore" ? 10000000 : 100000;

      await saveDailyCommitment({
        loginFiles: parseInt(dailyLoginFiles),
        loginAmount: parseInt(dailyLoginAmount) * loginMultiplier,
        disbursementFiles: parseInt(dailyDisbursementFiles),
        disbursementAmount: parseInt(dailyDisbursementAmount) * disbursementMultiplier,
      });

      toast.success("Daily commitment saved!");

      // On a holiday, if user submitted their commitment they are working today →
      // immediately trigger the check-in flow to mark them as present.
      // Keep form data populated so handleMarkPresent can re-use it if needed.
      const isHolidayToday = todayHoliday.isHoliday || attendanceStatus?.status === 'holiday';
      if (isHolidayToday && !attendanceStatus?.hasCheckedIn && !attendanceStatus?.isAbsent) {
        handleMarkPresent();
        return; // form cleared after check-in resolves
      }

      // Clear form
      setDailyLoginFiles("");
      setDailyLoginAmount("");
      setDailyDisbursementFiles("");
      setDailyDisbursementAmount("");
    } catch (error) {
      console.error('Error saving daily commitment:', error);
      toast.error("Failed to save daily commitment");
    }
  };

  const handleMonthlySubmit = async () => {
    if (!monthlyLoginFiles || !monthlyLoginAmount || !monthlyDisbursementFiles || !monthlyDisbursementAmount) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const loginMultiplier = monthlyLoginUnit === "crore" ? 10000000 : 100000;
      const disbursementMultiplier = monthlyDisbursementUnit === "crore" ? 10000000 : 100000;

      await saveMonthlyCommitment({
        loginFiles: parseInt(monthlyLoginFiles),
        loginAmount: parseInt(monthlyLoginAmount) * loginMultiplier,
        disbursementFiles: parseInt(monthlyDisbursementFiles),
        disbursementAmount: parseInt(monthlyDisbursementAmount) * disbursementMultiplier,
      });

      toast.success("Monthly commitment saved!");
      
      // Clear form
      setMonthlyLoginFiles("");
      setMonthlyLoginAmount("");
      setMonthlyDisbursementFiles("");
      setMonthlyDisbursementAmount("");
      
      // Notify parent component to refresh stats
      if (onCommitmentChange) {
        onCommitmentChange();
      }
    } catch (error) {
      console.error('Error saving monthly commitment:', error);
      toast.error("Failed to save monthly commitment");
    }
  };

  // Calculate percentages for daily (guarded against division by zero)
  const getDailyLoginPercentage = () => {
    if (!dailyCommitment || !dailyCommitment.loginAmount) return 0;
    return Math.min(100, Math.round((dailyTracking.loginAmountAchieved / dailyCommitment.loginAmount) * 100));
  };

  const getDailyDisbursementPercentage = () => {
    if (!dailyCommitment || !dailyCommitment.disbursementAmount) return 0;
    return Math.min(100, Math.round((dailyTracking.disbursementAmountAchieved / dailyCommitment.disbursementAmount) * 100));
  };

  const renderDailySubmitted = () => {
    return (
      <div className="space-y-3">
      {/* Live holiday override banner: admin marked today as holiday after user already submitted */}
      {todayHoliday.isHoliday && (
        <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <PartyPopper className="h-4 w-4 text-green-600" />
            <span className="text-sm font-semibold text-green-900">🌴 Holiday Today</span>
          </div>
          <p className="text-xs text-green-700">{todayHoliday.reason || 'Office holiday — marked by admin'}</p>
        </div>
      )}
      {/* Login Tracking */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="p-3 rounded-lg bg-accent/10 border border-accent/20"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <LogIn className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium">Login</span>
          </div>
          <span className="text-xs font-semibold text-accent">{getDailyLoginPercentage()}%</span>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{dailyTracking.loginFilesAchieved}/{dailyCommitment!.loginFiles} files</span>
          <span>{formatCurrency(dailyTracking.loginAmountAchieved)}/{formatCurrency(dailyCommitment!.loginAmount)}</span>
        </div>
        <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div 
            className="h-full bg-accent rounded-full transition-all duration-500"
            style={{ width: `${getDailyLoginPercentage()}%` }}
          />
        </div>
      </motion.div>

      {/* Disbursement Tracking */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="p-3 rounded-lg bg-success/10 border border-success/20"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-success" />
            <span className="text-sm font-medium">Disbursement</span>
          </div>
          <span className="text-xs font-semibold text-success">{getDailyDisbursementPercentage()}%</span>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{dailyTracking.disbursementFilesAchieved}/{dailyCommitment!.disbursementFiles} files</span>
          <span>{formatCurrency(dailyTracking.disbursementAmountAchieved)}/{formatCurrency(dailyCommitment!.disbursementAmount)}</span>
        </div>
        <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div 
            className="h-full bg-success rounded-full transition-all duration-500"
            style={{ width: `${getDailyDisbursementPercentage()}%` }}
          />
        </div>
      </motion.div>

        <div className="flex items-center justify-center gap-2">
          <p className="text-[10px] text-muted-foreground">
            Submitted at {dailyCommitment!.createdAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(
              `${userName || user?.name || 'My'} Commitment -\n` +
              `📁 Login Target: ${dailyCommitment!.loginFiles} files | ${formatCurrency(dailyCommitment!.loginAmount).replace('.0', '')}\n` +
              `💰 Disbursal Target: ${dailyCommitment!.disbursementFiles} files | ${formatCurrency(dailyCommitment!.disbursementAmount).replace('.0', '')}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-600 hover:text-green-700 flex-shrink-0"
            title="Share on WhatsApp"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </a>
        </div>
      </div>
    );
  };

  const renderDailyForm = () => {
    return (
      <div className="space-y-3">
      {/* Login Section */}
      <div className="p-3 rounded-lg bg-accent/5 border border-accent/10 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <LogIn className="h-4 w-4 text-accent" />
          <span className="text-sm font-medium">Login Target</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Files</Label>
            <Input
              type="number"
              placeholder="0"
              min="0"
              max="1000"
              value={dailyLoginFiles}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                  setDailyLoginFiles(e.target.value);
                }
              }}
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Amount</Label>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="0"
                min="0"
                max="1000"
                value={dailyLoginAmount}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                    setDailyLoginAmount(e.target.value);
                  }
                }}
                className="h-8 text-sm flex-1"
              />
              <Select value={dailyLoginUnit} onValueChange={(v) => setDailyLoginUnit(v as "lakh" | "crore")}>
                <SelectTrigger className="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lakh">Lakh</SelectItem>
                  <SelectItem value="crore">Crore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Disbursement Section */}
      <div className="p-3 rounded-lg bg-success/5 border border-success/10 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-4 w-4 text-success" />
          <span className="text-sm font-medium">Disbursement Target</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Files</Label>
            <Input
              type="number"
              placeholder="0"
              min="0"
              max="1000"
              value={dailyDisbursementFiles}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                  setDailyDisbursementFiles(e.target.value);
                }
              }}
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Amount</Label>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="0"
                min="0"
                max="1000"
                value={dailyDisbursementAmount}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                    setDailyDisbursementAmount(e.target.value);
                  }
                }}
                className="h-8 text-sm flex-1"
              />
              <Select value={dailyDisbursementUnit} onValueChange={(v) => setDailyDisbursementUnit(v as "lakh" | "crore")}>
                <SelectTrigger className="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lakh">Lakh</SelectItem>
                  <SelectItem value="crore">Crore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Holiday Banner */}
      {todayHoliday.isHoliday && (
        <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
          <div className="flex items-center justify-center gap-2 mb-0.5">
            <PartyPopper className="h-4 w-4 text-green-600" />
            <span className="text-sm font-semibold text-green-900">🌴 Holiday Today</span>
          </div>
          <p className="text-xs text-green-700">{todayHoliday.reason}</p>
        </div>
      )}

      {/* Buttons/Status section */}
      {(() => {
        const hasFormData = !!(dailyLoginFiles || dailyLoginAmount || dailyDisbursementFiles || dailyDisbursementAmount);
        // Combine live office holiday config + DB holiday record for robust detection
        const isHolidayToday = todayHoliday.isHoliday || attendanceStatus?.status === 'holiday';

        // PRIORITY 1: Holiday already confirmed in DB — show holiday banner + Save/Cancel so users can still submit commitment
        if (attendanceStatus?.status === 'holiday') {
          return (
            <div className="space-y-2">
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <PartyPopper className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-900">🌴 Holiday</span>
                </div>
                <p className="text-xs text-green-700">{todayHoliday.reason || 'Enjoy your holiday!'}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="h-10"
                  size="sm"
                  onClick={() => {
                    setDailyLoginFiles("");
                    setDailyLoginAmount("");
                    setDailyDisbursementFiles("");
                    setDailyDisbursementAmount("");
                  }}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  <span className="font-medium">Cancel</span>
                </Button>
                <Button
                  onClick={handleDailySubmit}
                  disabled={!hasFormData}
                  className="h-10 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <Check className="h-4 w-4 mr-1" />
                  <span className="font-medium">Save</span>
                </Button>
              </div>
            </div>
          );
        }

        // PRIORITY 2: Not yet checked in / not absent → show action buttons
        if (!attendanceStatus?.hasCheckedIn && !attendanceStatus?.isAbsent) {
          // On a holiday (live from office config) → Save/Cancel for commitment
          if (isHolidayToday) {
            return (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="h-10"
                  size="sm"
                  onClick={() => {
                    setDailyLoginFiles("");
                    setDailyLoginAmount("");
                    setDailyDisbursementFiles("");
                    setDailyDisbursementAmount("");
                  }}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  <span className="font-medium">Cancel</span>
                </Button>
                <Button
                  onClick={handleDailySubmit}
                  disabled={!hasFormData}
                  className="h-10 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <Check className="h-4 w-4 mr-1" />
                  <span className="font-medium">Save</span>
                </Button>
              </div>
            );
          }
          // Normal workday → Absent / Present buttons
          return (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleMarkAbsent}
                variant="outline"
                className="h-10 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                size="sm"
              >
                <XCircle className="h-4 w-4 mr-1" />
                <span className="font-medium">Absent</span>
              </Button>

              <Button
                onClick={handleMarkPresent}
                disabled={checkingIn || (!dailyCommitment && !hasFormData)}
                className="h-10 bg-green-600 hover:bg-green-700"
                size="sm"
              >
                {checkingIn ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-1" />
                )}
                <span className="font-medium">Present</span>
              </Button>
            </div>
          );
        }

        // PRIORITY 3: Absent status
        if (attendanceStatus?.isAbsent) {
          return (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm font-semibold text-red-900">Marked Absent</span>
              </div>
              <p className="text-xs text-red-700">You are marked as absent for today</p>
            </div>
          );
        }

        // PRIORITY 4: Checked in (present/late/away)
        if (attendanceStatus?.hasCheckedIn) {
          return (
            <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-semibold text-green-900">
                  Present {attendanceStatus.isLate && '(Late)'}
                </span>
              </div>
              {attendanceStatus.checkInTime && (
                <p className="text-xs text-green-700">
                  Checked in at {format(attendanceStatus.checkInTime, 'hh:mm a')}
                </p>
              )}
            </div>
          );
        }

        return null;
      })()}
      </div>
    );
  };

  const renderMonthlySubmitted = () => {
    return (
      <div className="space-y-3">
      {/* Monthly Login */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="p-3 rounded-lg bg-accent/10 border border-accent/20"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <LogIn className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium">Monthly Login Target</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{monthlyCommitment!.loginFiles} files</span>
          <span>{formatCurrency(monthlyCommitment!.loginAmount)}</span>
        </div>
      </motion.div>

      {/* Monthly Disbursement */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="p-3 rounded-lg bg-success/10 border border-success/20"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-success" />
            <span className="text-sm font-medium">Monthly Disbursement Target</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{monthlyCommitment!.disbursementFiles} files</span>
          <span>{formatCurrency(monthlyCommitment!.disbursementAmount)}</span>
        </div>
      </motion.div>

        <p className="text-[10px] text-muted-foreground text-center">
          Committed on {monthlyCommitment!.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          {monthlyEditable && " • Editable today"}
        </p>
      </div>
    );
  };

  const renderMonthlyForm = () => {
    return (
      <div className="space-y-3">
      {/* Login Section */}
      <div className="p-3 rounded-lg bg-accent/5 border border-accent/10 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <LogIn className="h-4 w-4 text-accent" />
          <span className="text-sm font-medium">Monthly Login Target</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Files</Label>
            <Input
              type="number"
              placeholder="0"
              min="0"
              max="1000"
              value={monthlyLoginFiles}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                  setMonthlyLoginFiles(e.target.value);
                }
              }}
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Amount</Label>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="0"
                min="0"
                max="1000"
                value={monthlyLoginAmount}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                    setMonthlyLoginAmount(e.target.value);
                  }
                }}
                className="h-8 text-sm flex-1"
              />
              <Select value={monthlyLoginUnit} onValueChange={(v) => setMonthlyLoginUnit(v as "lakh" | "crore")}>
                <SelectTrigger className="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lakh">Lakh</SelectItem>
                  <SelectItem value="crore">Crore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Disbursement Section */}
      <div className="p-3 rounded-lg bg-success/5 border border-success/10 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-4 w-4 text-success" />
          <span className="text-sm font-medium">Monthly Disbursement Target</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Files</Label>
            <Input
              type="number"
              placeholder="0"
              min="0"
              max="1000"
              value={monthlyDisbursementFiles}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                  setMonthlyDisbursementFiles(e.target.value);
                }
              }}
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Amount</Label>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="0"
                min="0"
                max="1000"
                value={monthlyDisbursementAmount}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (e.target.value === '' || (val >= 0 && val <= 1000)) {
                    setMonthlyDisbursementAmount(e.target.value);
                  }
                }}
                className="h-8 text-sm flex-1"
              />
              <Select value={monthlyDisbursementUnit} onValueChange={(v) => setMonthlyDisbursementUnit(v as "lakh" | "crore")}>
                <SelectTrigger className="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lakh">Lakh</SelectItem>
                  <SelectItem value="crore">Crore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

        <Button onClick={handleMonthlySubmit} className="w-full h-9" size="sm">
          <Check className="h-4 w-4 mr-1" />
          Submit Monthly
        </Button>
      </div>
    );
  };

  // Main component render
  return (
    <Card variant="elevated" className="w-full min-w-0 overflow-hidden">
      <CardHeader className={isCollapsed ? "pb-1 pt-3" : "pb-1"}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-accent" />
            <CardTitle className="text-base md:text-lg">
              {dailyCommitment ? "Today's Commitment" : "Commitment & Attendance"}
            </CardTitle>
          </div>
          {/* Collapse Toggle - Mobile Only */}
          {!disableCollapse && (
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden h-8 w-8 p-0"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        
        {/* Collapsed Summary - Mobile Only */}
        {isCollapsed && (
          <div className="md:hidden mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            {dailyCommitment ? (
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-green-600" />
                  Login: {dailyCommitment.loginFiles} files
                </span>
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-green-600" />
                  Disburse: {dailyCommitment.disbursementFiles} files
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">Tap to set daily targets</span>
            )}
          </div>
        )}
      </CardHeader>
      
      {/* Content for dialog mode (disableCollapse) - render directly without animation */}
      {disableCollapse && !isCollapsed && (
        <CardContent className="space-y-3">
          {/* Show loader until attendance data is ready in dialog mode */}
          {attendanceStatus === null ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "daily" | "monthly")}>
            <TabsList className="grid w-full grid-cols-2 h-9 bg-secondary/50">
              <TabsTrigger value="daily" className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <Target className="h-3 w-3 mr-1" />
                Daily
              </TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs data-[state=active]:bg-success data-[state=active]:text-success-foreground">
                <Calendar className="h-3 w-3 mr-1" />
                Monthly
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="daily" className="mt-3">
              {(dailyCommitment && (attendanceStatus?.hasCheckedIn || attendanceStatus?.isAbsent)) 
                ? renderDailySubmitted() 
                : renderDailyForm()
              }
            </TabsContent>
            
            <TabsContent value="monthly" className="mt-3">
              {monthlyCommitment && !monthlyEditable 
                ? renderMonthlySubmitted() 
                : renderMonthlyForm()
              }
            </TabsContent>
          </Tabs>
          )}
        </CardContent>
      )}

      {/* Content for homepage mobile - with collapse animation */}
      {!disableCollapse && (
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden md:hidden"
          >
            <CardContent className="space-y-3">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "daily" | "monthly")}>
                <TabsList className="grid w-full grid-cols-2 h-9 bg-secondary/50">
                  <TabsTrigger value="daily" className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                    <Target className="h-3 w-3 mr-1" />
                    Daily
                  </TabsTrigger>
                  <TabsTrigger value="monthly" className="text-xs data-[state=active]:bg-success data-[state=active]:text-success-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    Monthly
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="daily" className="mt-3">
                  {(dailyCommitment && (attendanceStatus?.hasCheckedIn || attendanceStatus?.isAbsent)) 
                    ? renderDailySubmitted() 
                    : renderDailyForm()
                  }
                </TabsContent>
                
                <TabsContent value="monthly" className="mt-3">
                  {monthlyCommitment && !monthlyEditable 
                    ? renderMonthlySubmitted() 
                    : renderMonthlyForm()
                  }
                </TabsContent>
              </Tabs>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
      )}
      
      {/* Desktop: Always show content (hidden in dialog mode since direct render block handles it) */}
      <div className={disableCollapse ? "hidden" : "hidden md:block"}>
        <CardContent className="space-y-3">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "daily" | "monthly")}>
            <TabsList className="grid w-full grid-cols-2 h-9 bg-secondary/50">
              <TabsTrigger value="daily" className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <Target className="h-3 w-3 mr-1" />
                Daily
              </TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs data-[state=active]:bg-success data-[state=active]:text-success-foreground">
                <Calendar className="h-3 w-3 mr-1" />
                Monthly
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="daily" className="mt-3">
              {(dailyCommitment && (attendanceStatus?.hasCheckedIn || attendanceStatus?.isAbsent)) 
                ? renderDailySubmitted() 
                : renderDailyForm()
              }
            </TabsContent>
            
            <TabsContent value="monthly" className="mt-3">
              {monthlyCommitment && !monthlyEditable 
                ? renderMonthlySubmitted() 
                : renderMonthlyForm()
              }
            </TabsContent>
          </Tabs>
        </CardContent>
      </div>

      {/* Dialogs */}
      <AbsentReasonDialog
        open={absentDialogOpen}
        onOpenChange={setAbsentDialogOpen}
        userId={user?.userId || ''}
        userName={userName}
        officeId={userOfficeId}
        officeName={userOfficeName}
        onAbsentMarked={handleAbsentMarked}
      />

      <AttendanceReasonDialog
        open={reasonDialogOpen}
        onOpenChange={setReasonDialogOpen}
        isLate={pendingCheckInData?.isLate || false}
        isOutsideRange={pendingCheckInData?.isOutsideRange || false}
        distance={pendingCheckInData?.distance}
        allowedRadius={userOffice?.attendanceSettings?.officeRadius ?? DEFAULT_OFFICE_ATTENDANCE.officeRadius}
        checkInTime={pendingCheckInData?.checkInTime || new Date()}
        onConfirm={handleConfirmAttendanceWithReason}
      />
    </Card>
  );
}